import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Eye,
  Heart,
  ShareNetwork,
  CaretLeft,
  PaperPlaneTilt,
  SealCheck,
  CurrencyCircleDollar,
  Gift,
  Microphone,
  Play,
  Plus,
  HandWaving,
  X,
  VideoCamera,
  MicrophoneSlash,
} from "@phosphor-icons/react";
import Sidebar from "@/components/Sidebar";
import BottomNav from "@/components/BottomNav";
import { liveShows, liveChatByShow, currentUser, artists } from "@/data/mockData";

export default function LiveRoom() {
  const { id } = useParams();
  const show = liveShows.find((s) => s.id === id) || liveShows[0];
  const chat = liveChatByShow[show.id] || liveChatByShow.live1;
  const [msg, setMsg] = useState("");
  const [liked, setLiked] = useState(false);
  // On-stage collab state: host + up to 3 guests
  const initialGuests = artists
    .filter((a) => a.slug !== show.artist.slug)
    .slice(0, 2);
  const [onStage, setOnStage] = useState([show.artist, ...initialGuests]);
  const [requests, setRequests] = useState(
    artists
      .filter(
        (a) => a.slug !== show.artist.slug && !initialGuests.find((g) => g.slug === a.slug)
      )
      .slice(0, 3)
  );
  const [selfMuted, setSelfMuted] = useState(true);
  const [requested, setRequested] = useState(false);

  const approveGuest = (guest) => {
    if (onStage.length >= 4) return;
    setOnStage([...onStage, guest]);
    setRequests(requests.filter((r) => r.slug !== guest.slug));
  };
  const removeGuest = (slug) => {
    if (slug === show.artist.slug) return; // cannot remove host
    setOnStage(onStage.filter((g) => g.slug !== slug));
  };
  const declineRequest = (slug) => {
    setRequests(requests.filter((r) => r.slug !== slug));
  };

  return (
    <div className="min-h-screen bg-[#050505] text-[#F4F4F5]">
      <Sidebar />
      <main className="md:pl-[240px] lg:pl-[260px] pb-20 md:pb-0">
        <div className="max-w-[1600px] mx-auto" data-testid="live-room">
          {/* Top bar */}
          <header className="flex items-center justify-between gap-4 p-4 border-b border-[#27272A] sticky top-0 bg-[#050505]/90 backdrop-blur-lg z-20">
            <Link
              to="/live"
              className="h-9 w-9 border border-[#27272A] hover:border-[#FF3E00] inline-flex items-center justify-center"
            >
              <CaretLeft size={16} />
            </Link>
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] bg-[#FF0033] text-white px-2 py-1">
                <span className="h-1.5 w-1.5 bg-white live-dot" />
                LIVE
              </span>
              <span className="font-mono text-[11px] text-[#A1A1AA] tracking-wider inline-flex items-center gap-1 shrink-0">
                <Eye size={13} weight="fill" />
                {show.viewers.toLocaleString("es")}
              </span>
              <span className="font-mono text-[10px] text-[#525252] tracking-wider hidden md:inline">
                · {show.started}
              </span>
            </div>
            <button className="btn-brutal font-mono uppercase text-[10px] tracking-[0.22em] border border-[#F4F4F5] hover:bg-[#F4F4F5] hover:text-black px-3 py-2 inline-flex items-center gap-2">
              <ShareNetwork size={13} /> Compartir
            </button>
          </header>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] min-h-[calc(100vh-60px)]">
            {/* Stream */}
            <section className="flex flex-col border-r border-[#27272A]">
              <div className="relative bg-black aspect-video overflow-hidden">
                <img
                  src={show.cover}
                  alt={show.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/20" />
                <button
                  className="absolute inset-0 flex items-center justify-center group"
                  aria-label="Reproducir"
                >
                  <div className="h-20 w-20 bg-[#FF3E00] text-white flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play size={32} weight="fill" />
                  </div>
                </button>
                {/* Floating reactions */}
                <div className="absolute bottom-4 right-4 flex flex-col gap-2">
                  {["❤", "🔥", "✨"].map((e, i) => (
                    <span
                      key={i}
                      className="h-10 w-10 bg-black/50 backdrop-blur-sm border border-white/20 inline-flex items-center justify-center text-lg fade-up"
                      style={{ animationDelay: `${i * 0.1}s` }}
                    >
                      {e}
                    </span>
                  ))}
                </div>
              </div>

              {/* ON STAGE · Invitados */}
              <section
                data-testid="on-stage-section"
                className="border-t border-[#27272A] bg-[#0a0a0a]"
              >
                <header className="flex items-center justify-between px-5 py-3 border-b border-[#27272A]">
                  <div className="flex items-center gap-2">
                    <HandWaving size={16} weight="fill" className="text-[#FF3E00]" />
                    <h3 className="font-display font-bold uppercase tracking-tight text-sm">
                      En el escenario
                    </h3>
                    <span className="font-mono text-[10px] text-[#A1A1AA] tracking-wider">
                      {onStage.length}/4 · COLLAB LIVE
                    </span>
                  </div>
                  {!requested ? (
                    <button
                      data-testid="request-stage-btn"
                      onClick={() => setRequested(true)}
                      disabled={onStage.length >= 4}
                      className={`btn-brutal font-mono uppercase text-[10px] tracking-[0.22em] px-3 py-2 inline-flex items-center gap-2 ${
                        onStage.length >= 4
                          ? "bg-[#121212] text-[#525252] cursor-not-allowed"
                          : "bg-[#FF3E00] text-white"
                      }`}
                    >
                      <Plus size={12} weight="bold" /> Unirme al escenario
                    </button>
                  ) : (
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] bg-[#00FF66]/10 border border-[#00FF66] text-[#00FF66] px-3 py-2 inline-flex items-center gap-2">
                      <span className="h-1.5 w-1.5 bg-[#00FF66] live-dot" />
                      Solicitud enviada
                    </span>
                  )}
                </header>

                {/* Stage slots */}
                <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {onStage.map((guest) => {
                    const isHost = guest.slug === show.artist.slug;
                    return (
                      <div
                        key={guest.slug}
                        data-testid={`stage-slot-${guest.slug}`}
                        className={`relative aspect-[4/5] border group overflow-hidden ${
                          isHost
                            ? "border-[#FF3E00]"
                            : "border-[#27272A] hover:border-[#F4F4F5]"
                        }`}
                      >
                        <img
                          src={guest.avatar}
                          alt={guest.name}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />

                        {/* Top badge */}
                        <div className="absolute top-2 left-2 right-2 flex items-center justify-between gap-2">
                          {isHost ? (
                            <span className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.2em] bg-[#FF3E00] text-white px-1.5 py-0.5">
                              HOST
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.2em] bg-black/70 backdrop-blur-sm border border-white/30 text-white px-1.5 py-0.5">
                              <span className="h-1 w-1 bg-[#FF0033] live-dot" />
                              LIVE
                            </span>
                          )}
                          {!isHost ? (
                            <button
                              aria-label={`Retirar ${guest.name}`}
                              onClick={() => removeGuest(guest.slug)}
                              className="h-6 w-6 bg-black/60 backdrop-blur-sm border border-white/20 text-white/80 hover:bg-[#FF0033] hover:border-[#FF0033] inline-flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
                            >
                              <X size={10} weight="bold" />
                            </button>
                          ) : null}
                        </div>

                        {/* Mic waveform */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-1 h-8">
                          {[0, 1, 2, 3, 4].map((b) => (
                            <span
                              key={b}
                              className="w-1 bg-white/90 live-dot"
                              style={{
                                animationDelay: `${b * 0.15}s`,
                                height: `${20 + ((b * 7) % 16)}px`,
                              }}
                            />
                          ))}
                        </div>

                        {/* Bottom identity */}
                        <div className="absolute bottom-0 inset-x-0 p-2">
                          <div className="font-display font-bold text-[13px] truncate flex items-center gap-1">
                            {guest.name}
                            {guest.verified ? (
                              <SealCheck
                                size={11}
                                weight="fill"
                                className="text-[#FF3E00] shrink-0"
                              />
                            ) : null}
                          </div>
                          <div className="font-mono text-[9px] text-[#FF3E00] tracking-wider truncate">
                            @{guest.handle}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Empty slots */}
                  {Array.from({ length: Math.max(0, 4 - onStage.length) }).map(
                    (_, i) => (
                      <div
                        key={`slot-${i}`}
                        className="relative aspect-[4/5] border border-dashed border-[#27272A] bg-[#050505] flex flex-col items-center justify-center gap-2 text-[#525252]"
                      >
                        <Plus size={18} />
                        <span className="font-mono text-[9px] uppercase tracking-[0.22em]">
                          Slot libre
                        </span>
                      </div>
                    )
                  )}
                </div>

                {/* Self controls */}
                {requested ? (
                  <div className="px-4 pb-4">
                    <div className="flex items-center justify-between gap-3 bg-[#121212] border border-[#27272A] px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <img
                          src={currentUser.avatar}
                          alt=""
                          className="h-7 w-7 object-cover border border-[#27272A]"
                        />
                        <div className="min-w-0">
                          <div className="font-display font-bold text-xs">
                            {currentUser.name}
                          </div>
                          <div className="font-mono text-[9px] text-[#A1A1AA] tracking-wider">
                            ESPERANDO APROBACIÓN…
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setSelfMuted((v) => !v)}
                          aria-label="mic"
                          className={`h-8 w-8 border inline-flex items-center justify-center ${
                            selfMuted
                              ? "border-[#FF0033] text-[#FF0033]"
                              : "border-[#00FF66] text-[#00FF66]"
                          }`}
                        >
                          {selfMuted ? (
                            <MicrophoneSlash size={14} weight="fill" />
                          ) : (
                            <Microphone size={14} weight="fill" />
                          )}
                        </button>
                        <button
                          aria-label="cam"
                          className="h-8 w-8 border border-[#27272A] text-[#A1A1AA] hover:border-[#F4F4F5] hover:text-white inline-flex items-center justify-center"
                        >
                          <VideoCamera size={14} />
                        </button>
                        <button
                          onClick={() => setRequested(false)}
                          className="font-mono uppercase text-[10px] tracking-[0.22em] px-3 py-2 border border-[#27272A] hover:border-[#FF0033] text-[#A1A1AA] hover:text-[#FF0033]"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* Pending requests (host-view mock) */}
                {requests.length > 0 ? (
                  <div className="px-4 pb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em]">
                        // SOLICITUDES PARA SUBIR ({requests.length})
                      </span>
                    </div>
                    <ul className="space-y-1">
                      {requests.map((r) => (
                        <li
                          key={r.slug}
                          data-testid={`request-${r.slug}`}
                          className="flex items-center gap-3 border border-[#27272A] bg-[#050505] px-3 py-2"
                        >
                          <img
                            src={r.avatar}
                            alt={r.name}
                            className="h-8 w-8 object-cover border border-[#27272A] shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-display font-bold text-xs truncate flex items-center gap-1">
                              {r.name}
                              {r.verified ? (
                                <SealCheck
                                  size={10}
                                  weight="fill"
                                  className="text-[#FF3E00] shrink-0"
                                />
                              ) : null}
                            </div>
                            <div className="font-mono text-[9px] text-[#A1A1AA] tracking-wider truncate">
                              @{r.handle} · {r.role.toUpperCase()}
                            </div>
                          </div>
                          <button
                            data-testid={`approve-${r.slug}`}
                            onClick={() => approveGuest(r)}
                            disabled={onStage.length >= 4}
                            className={`font-mono uppercase text-[9px] tracking-[0.22em] px-2.5 py-1.5 ${
                              onStage.length >= 4
                                ? "bg-[#121212] text-[#525252] cursor-not-allowed"
                                : "bg-[#00FF66] text-black hover:bg-white"
                            }`}
                          >
                            Subir
                          </button>
                          <button
                            onClick={() => declineRequest(r.slug)}
                            aria-label="Descartar"
                            className="h-7 w-7 border border-[#27272A] text-[#A1A1AA] hover:border-[#FF0033] hover:text-[#FF0033] inline-flex items-center justify-center"
                          >
                            <X size={11} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </section>
              <div className="p-5 md:p-6">
                <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em] mb-2">
                  {show.discipline} · EN VIVO
                </div>
                <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter uppercase leading-tight">
                  {show.title}
                </h1>
                <div className="font-mono text-[11px] text-[#A1A1AA] tracking-wider mt-2">
                  // DESDE {show.venue.toUpperCase()}
                </div>

                {/* Artist row */}
                <div className="flex items-center justify-between gap-3 mt-6 pt-6 border-t border-[#27272A] flex-wrap">
                  <Link
                    to={`/artista/${show.artist.slug}`}
                    className="flex items-center gap-3 min-w-0"
                  >
                    <img
                      src={show.artist.avatar}
                      alt={show.artist.name}
                      className="h-12 w-12 object-cover border border-[#27272A]"
                    />
                    <div className="min-w-0">
                      <div className="font-display font-bold flex items-center gap-1.5">
                        {show.artist.name}
                        {show.artist.verified ? (
                          <SealCheck size={14} weight="fill" className="text-[#FF3E00]" />
                        ) : null}
                      </div>
                      <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider">
                        @{show.artist.handle} · {show.artist.followers.toLocaleString("es")} seguidores
                      </div>
                    </div>
                  </Link>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setLiked((v) => !v)}
                      data-testid="live-like"
                      className={`btn-brutal font-mono uppercase text-[10px] tracking-[0.22em] px-4 py-2 inline-flex items-center gap-2 ${
                        liked
                          ? "bg-[#FF3E00] text-white"
                          : "border border-[#27272A] hover:border-[#FF3E00] text-[#A1A1AA] hover:text-[#FF3E00]"
                      }`}
                    >
                      <Heart size={13} weight={liked ? "fill" : "regular"} />
                      {liked ? "Te gusta" : "Me gusta"}
                    </button>
                    <button className="btn-brutal font-mono uppercase text-[10px] tracking-[0.22em] px-4 py-2 border border-[#F4F4F5] hover:bg-[#F4F4F5] hover:text-black inline-flex items-center gap-2">
                      <Gift size={13} /> Tip
                    </button>
                    <button className="btn-brutal font-mono uppercase text-[10px] tracking-[0.22em] px-4 py-2 bg-[#FF3E00] text-white">
                      + Seguir
                    </button>
                  </div>
                </div>

                {/* Support tiers */}
                <div className="mt-6 grid grid-cols-3 gap-2">
                  {[
                    { label: "Aplaudir", amount: "$1" },
                    { label: "Tip", amount: "$5" },
                    { label: "Patrocinar", amount: "$20" },
                  ].map((t) => (
                    <button
                      key={t.label}
                      className="border border-[#27272A] hover:border-[#FF3E00] p-3 group transition-colors"
                    >
                      <CurrencyCircleDollar
                        size={20}
                        className="mx-auto text-[#A1A1AA] group-hover:text-[#FF3E00]"
                      />
                      <div className="font-display font-bold text-sm mt-1">
                        {t.amount}
                      </div>
                      <div className="font-mono text-[9px] text-[#A1A1AA] tracking-wider uppercase">
                        {t.label}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </section>

            {/* Live chat */}
            <aside className="flex flex-col min-h-0 max-h-[calc(100vh-60px)]">
              <header className="flex items-center justify-between p-4 border-b border-[#27272A]">
                <div>
                  <h3 className="font-display font-bold uppercase tracking-tight">
                    Chat en vivo
                  </h3>
                  <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider">
                    {chat.length} · ACTIVO
                  </div>
                </div>
                <Microphone size={16} className="text-[#00FF66]" />
              </header>

              <div
                data-testid="live-chat-messages"
                className="flex-1 overflow-auto p-3 space-y-2 bg-[#050505]"
              >
                {chat.map((m) => (
                  <div key={m.id} className="flex gap-2 items-start fade-up">
                    <img
                      src={m.artist.avatar}
                      alt={m.artist.name}
                      className="h-7 w-7 object-cover border border-[#27272A] shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-display font-bold text-xs">
                          {m.artist.name}
                        </span>
                        <span className="font-mono text-[9px] text-[#525252]">
                          {m.time}
                        </span>
                      </div>
                      <p className="text-[13px] text-[#E5E5E5] leading-snug">
                        {m.text}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-[#27272A] p-3 flex items-center gap-2">
                <img
                  src={currentUser.avatar}
                  alt=""
                  className="h-8 w-8 object-cover border border-[#27272A] shrink-0"
                />
                <input
                  data-testid="live-chat-input"
                  value={msg}
                  onChange={(e) => setMsg(e.target.value)}
                  placeholder="Escribe..."
                  className="flex-1 bg-[#0a0a0a] border border-[#27272A] focus:border-[#FF3E00] focus:outline-none px-3 py-2 font-mono text-xs"
                />
                <button
                  data-testid="live-chat-send"
                  disabled={!msg.trim()}
                  className={`font-mono uppercase text-[10px] tracking-[0.2em] px-3 py-2 ${
                    msg.trim() ? "bg-[#FF3E00] text-white" : "bg-[#121212] text-[#525252]"
                  }`}
                >
                  <PaperPlaneTilt size={14} weight="bold" />
                </button>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <BottomNav />
    </div>
  );
}
